import React from 'react';
import { Form } from '@dashboard/components/Formik';
import usePage from '@dashboard/hooks/usePage';
import { uniqueId } from 'lodash';
import { formatPriceInput } from '@dashboard/services/formatting';
import { formatPriceForInput } from '@dashboard/services/formatting/price';
import { getPageCurrency } from '@dashboard/lib/getPageCurrency';

const defaultPaymentOptions = [
  {
    key: uniqueId(),
    type: 'full',
    enabled: true,
    name: 'Pay in full',
    description: 'Pay the full amount now',
    instructions: '',
    showPaymentButton: true,
  },
  {
    key: uniqueId(),
    type: 'manual',
    manualType: 'invoice',
    enabled: false,
    name: 'Pay via invoice',
    description: 'Payment due in 30 days',
    instructions: 'Bank details will be sent via email after checkout.',
    showPaymentButton: true,
  },
  {
    key: uniqueId(),
    type: 'partial',
    enabled: false,
    name: 'Pay a deposit',
    partialAmount: 5000,
    description: 'Pay a deposit now and the rest later',
    instructions: 'Pay the rest of your balance at a later date.',
    showPaymentButton: true,
  },
  {
    key: uniqueId(),
    type: 'manual',
    manualType: 'cash_on_delivery',
    enabled: false,
    name: 'Cash on delivery',
    description: 'Pay when you receive your order',
    instructions: 'Pay the full amount when your order is delivered.',
    showPaymentButton: true,
  },
];

function getInitialValues(page) {
  const paymentOptions = defaultPaymentOptions.map(defaultOption => {
    const existingOption = page.paymentOptions?.find(
      option =>
        option.type === defaultOption.type && option.manualType === defaultOption.manualType,
    );
    return existingOption ? { ...defaultOption, ...existingOption } : defaultOption;
  });
  const enabledOptions = paymentOptions.filter(option => option.enabled);

  let selectedOption = 'full';
  if (enabledOptions.length === 1) {
    selectedOption =
      enabledOptions[0].type === 'full'
        ? 'full'
        : enabledOptions[0].type === 'manual' && enabledOptions[0].manualType === 'invoice'
        ? 'invoice'
        : enabledOptions[0].type === 'manual' && enabledOptions[0].manualType === 'cash_on_delivery'
        ? 'cash_on_delivery'
        : 'partial';
  } else if (enabledOptions.length === 2) {
    if (
      enabledOptions.some(o => o.type === 'full') &&
      enabledOptions.some(o => o.type === 'manual' && o.manualType === 'invoice')
    ) {
      selectedOption = 'full_or_invoice';
    } else if (
      enabledOptions.some(o => o.type === 'full') &&
      enabledOptions.some(o => o.type === 'partial')
    ) {
      selectedOption = 'full_or_partial';
    } else if (
      enabledOptions.some(o => o.type === 'full') &&
      enabledOptions.some(o => o.type === 'manual' && o.manualType === 'cash_on_delivery')
    ) {
      selectedOption = 'full_or_cash_on_delivery';
    }
  }

  const currency = getPageCurrency(page);

  return {
    selectedOption,
    paymentOptions: paymentOptions.map(option => ({
      ...option,
      partialAmount:
        option.type === 'partial'
          ? formatPriceForInput({
              amount: option.partialAmount,
              currency,
              style: 'decimal',
            })
          : null,
    })),
  };
}

function getIsPaymentOptionEnabled(option, selectedOption) {
  switch (selectedOption) {
    case 'full':
      return option.type === 'full';
    case 'invoice':
      return option.type === 'manual' && option.manualType === 'invoice';
    case 'partial':
      return option.type === 'partial';
    case 'full_or_invoice':
      return (
        option.type === 'full' || (option.type === 'manual' && option.manualType === 'invoice')
      );
    case 'full_or_partial':
      return option.type === 'full' || option.type === 'partial';
    case 'cash_on_delivery':
      return option.type === 'manual' && option.manualType === 'cash_on_delivery';
    case 'full_or_cash_on_delivery':
      return (
        option.type === 'full' ||
        (option.type === 'manual' && option.manualType === 'cash_on_delivery')
      );
    default:
      return false;
  }
}

const PaymentOptionsForm = () => {
  const { page, updatePage } = usePage();

  const currency = getPageCurrency(page);

  return (
    <Form
      initialValues={getInitialValues(page)}
      onSubmit={async (values, actions) => {
        const updatedPaymentOptions = values.paymentOptions.map(option => ({
          ...option,
          enabled: getIsPaymentOptionEnabled(option, values.selectedOption),
          partialAmount:
            option.type === 'partial' ? formatPriceInput(option.partialAmount, currency) : null,
          showPaymentButton:
            values.selectedOption === 'invoice' ||
            values.selectedOption === 'partial' ||
            values.selectedOption === 'cash_on_delivery'
              ? option.showPaymentButton
              : true,
        }));

        const { data, error } = await updatePage.mutateAsync({
          paymentOptions: updatedPaymentOptions,
        });

        actions.resetForm({
          values: {
            ...values,
            paymentOptions: updatedPaymentOptions.map(option => ({
              ...option,
              partialAmount: option.partialAmount
                ? formatPriceForInput({
                    amount: option.partialAmount,
                    currency,
                    style: 'decimal',
                  })
                : null,
            })),
          },
        });

        return { data, error };
      }}
      fields={({ values, setFieldValue }) => [
        {
          type: 'radio',
          name: 'selectedOption',
          label: 'Payment options',
          options: [
            { value: 'full', label: 'Pay now only' },
            { value: 'full_or_invoice', label: 'Pay now or pay via invoice' },
            { value: 'full_or_partial', label: 'Pay now or pay a deposit' },
            { value: 'full_or_cash_on_delivery', label: 'Pay now or cash on delivery' },
            { value: 'invoice', label: 'Pay via invoice only' },
            { value: 'partial', label: 'Pay a deposit only' },
            { value: 'cash_on_delivery', label: 'Cash on delivery only' },
          ],
        },
        {
          visible: values.selectedOption !== 'full',
          type: 'optionList',
          name: 'paymentOptions',
          collapseOptions: true,
          allowDelete: false,
          allowAdd: false,
          allowReorder: true,
          hideIfDisabled: true,
          options: values.paymentOptions.map(option => ({
            ...option,
            enabled: getIsPaymentOptionEnabled(option, values.selectedOption),
          })),
          onChange: options => setFieldValue('paymentOptions', options),
          fields: ({ values: optionValues }) => [
            { type: 'text', name: 'name', label: 'Name' },
            {
              type: 'number',
              name: 'partialAmount',
              label: 'Deposit amount',
              visible: optionValues?.type === 'partial',
              required: optionValues?.type === 'partial' ? true : false,
            },
            { type: 'text', name: 'description', label: 'Description' },
            {
              type: 'textarea',
              name: 'instructions',
              label: 'Payment instructions',
              autosize: true,
              minRows: 2,
            },
            {
              type: 'checkbox',
              name: 'showPaymentButton',
              label:
                optionValues.type === 'partial'
                  ? 'Show deposit payment option'
                  : optionValues.type === 'manual' && optionValues.manualType === 'invoice'
                  ? 'Show invoice payment option'
                  : optionValues.type === 'manual' && optionValues.manualType === 'cash_on_delivery'
                  ? 'Show cash on delivery payment option'
                  : 'Show payment option',
              visible:
                values.selectedOption === 'invoice' ||
                values.selectedOption === 'partial' ||
                values.selectedOption === 'cash_on_delivery',
            },
          ],
        },
      ]}
      submitButton={{
        label: 'Save',
        appearance: 'primary',
      }}
    />
  );
};

export default PaymentOptionsForm;
